import {
  IExercise,
  ILesson,
  ISection,
  IUserScheduled,
  TIME_HOURS_LS,
  TIME_MINUTE_LS,
} from "../type";

export function isCompletedLesson(exercises: IExercise[]) {
  if (!exercises || exercises.length === 0) return false;
  let completed = true;
  for (let i = 0; i < exercises.length; i++) {
    const ex = exercises[i];
    // Проверяем, есть ли запись о выполнении задания
    if (!ex.UserComplete_ExercisesRelation || ex.UserComplete_ExercisesRelation.length === 0) {
      completed = false;
      break; // Прерываем цикл, если нашли невыполненное задание
    }
  }
  return completed;
}

/**
 * Последнее расписание модуля, у которого дата старта уже наступила (по локальной логике времени урока).
 * Именно его round должен отображаться в «Ступень N» и в расчёте прогресса — у разных модулей круги расходятся.
 */
export function getActiveScheduleForSection(
  section: ISection | undefined
): IUserScheduled | undefined {
  if (!section?.UserLessonsScheduled?.length) return undefined;
  const today = new Date();
  const sorted = [...section.UserLessonsScheduled].sort(
    (a, b) => +new Date(a.start_date) - +new Date(b.start_date)
  );
  let active: IUserScheduled | undefined;
  for (const sh of sorted) {
    const date = new Date(sh.start_date);
    var h = today.getTimezoneOffset() / 60;
    date.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    if (+date <= +today) {
      active = sh;
    } else {
      break;
    }
  }
  return active;
}

/**
 * Самое «свежее» уже начавшееся расписание среди всех модулей (максимальный start_date <= сейчас).
 * Используем как фокус главной (привычка / текущий модуль), чтобы не залипать на прошлом круге.
 */
export function getLatestStartedSchedule(
  sections: ISection[]
): IUserScheduled | undefined {
  const today = new Date();
  let best: IUserScheduled | undefined;
  let bestTime = -Infinity;
  for (const s of sections) {
    for (const u of s.UserLessonsScheduled || []) {
      const date = new Date(u.start_date);
      var h = today.getTimezoneOffset() / 60;
      date.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
      const t = date.getTime();
      if (t <= +today && t >= bestTime) {
        bestTime = t;
        best = u;
      }
    }
  }
  return best;
}

export function calculateComplitedLesson(section: ISection, round: number, allSections?: ISection[]) {
  let lockedSection = false;
  let today = new Date();
  const shed = section.UserLessonsScheduled.find(
    (sh) => sh.round === round
  ) as IUserScheduled;
  
  // Если нет расписания для этого круга - модуль заблокирован
  if (!shed) {
    return { complitedLesson: 0, lockedSection: false };
  }
  
  // Проверяем, пройден ли модуль (все уроки выполнены)
  const lessons = section.Lesson_SectionRelation.filter(
    (l) => l.round === shed.round
  );
  const complitedLesson = lessons.filter((l) =>
    isCompletedLesson(l.Exercise_LessonRelation)
  ).length;
  const allLessonsCompleted = lessons.length > 0 && complitedLesson === lessons.length;
  
  // Если модуль уже пройден - доступ открыт всегда (можно зайти в любое время на протяжении круга)
  if (allLessonsCompleted) {
    return { complitedLesson, lockedSection: true };
  }
  
  // Если модуль не пройден - проверяем только дату начала
  // Нельзя войти в модуль, который не подошел по расписанию (нельзя раньше срока)
  let targetDate = new Date(shed.start_date);
  var h = today.getTimezoneOffset() / 60;
  targetDate.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
  
  // Если дата начала еще не наступила - модуль заблокирован
  if (targetDate > today) {
    lockedSection = false;
  } else {
    // Дата начала наступила - доступ открыт (не проверяем последовательность)
    lockedSection = true;
  }
  
  const daysDifference = Math.floor(
    Math.abs((today.getTime() - targetDate.getTime()) / (1000 * 3600 * 24))
  );
  if (daysDifference > 6) return { complitedLesson: 7, lockedSection };
  
  return { complitedLesson, lockedSection };
}

export function getCurrentSection(
  sections: ISection[],
  scheduled?: IUserScheduled
): ISection {
  const shed = scheduled ?? getCurrentScheduled(sections);
  return sections.find((s) => s.id === shed.SectionId) as ISection;
}

export function getCurrentScheduled(sections: ISection[]): IUserScheduled {
  const today = new Date();
  const all_sched: IUserScheduled[] = [];
  sections.forEach((s) => {
    s.UserLessonsScheduled.forEach((u) => {
      all_sched.push(u);
    });
  });
  all_sched.sort((a, b) => +new Date(a.start_date) - +new Date(b.start_date));
  for (let i = 0; i < all_sched.length; i++) {
    const date = new Date(all_sched[i].start_date);
    var h = today.getTimezoneOffset() / 60;
    date.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    if (+date > +today) {
      return all_sched[i - 1];
    }
  }
  return all_sched[all_sched.length - 1];
}

function sortLessonsBySequence(lessons: ILesson[]): ILesson[] {
  return [...lessons].sort(
    (a, b) =>
      Number(a.sequence_number__lesson || 0) - Number(b.sequence_number__lesson || 0) ||
      Number(a.id || 0) - Number(b.id || 0)
  );
}

/**
 * Уроки модуля для круга из расписания. Если для round в БД нет уроков (частый кейс:
 * schedule.round=3, а в lesson только 1–2) — берём ближайший круг с контентом.
 */
export function resolveLessonsForScheduledRound(
  section: ISection | undefined,
  scheduledRound: number
): { lessons: ILesson[]; effectiveRound: number; usedFallback: boolean } {
  const round = Number(scheduledRound);
  if (!section) {
    return { lessons: [], effectiveRound: round, usedFallback: false };
  }
  const all = section.Lesson_SectionRelation || [];
  let lessons = all.filter((l) => Number(l.round) === round);
  if (lessons.length > 0) {
    return { lessons: sortLessonsBySequence(lessons), effectiveRound: round, usedFallback: false };
  }

  const rounds = Array.from(
    new Set(all.map((l) => Number(l.round)).filter((r) => Number.isFinite(r)))
  ).sort((a, b) => a - b);
  if (rounds.length === 0) {
    return { lessons: [], effectiveRound: round, usedFallback: false };
  }

  const fallbackRound =
    rounds.filter((r) => r <= round).pop() ??
    rounds.find((r) => r >= round) ??
    rounds[rounds.length - 1];

  lessons = all.filter((l) => Number(l.round) === fallbackRound);
  return {
    lessons: sortLessonsBySequence(lessons),
    effectiveRound: Number(fallbackRound),
    usedFallback: Number(fallbackRound) !== round,
  };
}

/** Позиция урока в полосе привычки относительно первого дня круга (без сброса «ind - 7»). */
export function habitLessonDayIndex(lesson: ILesson, orderedLessons: ILesson[]): number {
  const seq = Number(lesson.sequence_number__lesson);
  if (Number.isFinite(seq) && seq > 0 && orderedLessons.length > 0) {
    const minSeq = Math.min(
      ...orderedLessons.map((l) => Number(l.sequence_number__lesson) || seq)
    );
    return Math.max(0, seq - minSeq);
  }
  const idx = orderedLessons.findIndex((l) => l.id === lesson.id);
  return idx >= 0 ? idx : 0;
}

export function allLessonsCompletedInList(lessons: ILesson[]): boolean {
  return (
    lessons.length > 0 &&
    lessons.every((l) => isCompletedLesson(l.Exercise_LessonRelation))
  );
}

/** Расписание модуля для указанной ступени (круга). */
export function getScheduleForSectionRound(
  section: ISection | undefined,
  round: number
): IUserScheduled | undefined {
  if (!section?.UserLessonsScheduled?.length) return undefined;
  const exact = section.UserLessonsScheduled.find(
    (s) => Number(s.round) === Number(round)
  );
  if (exact) return exact;
  return getActiveScheduleForSection(section);
}

export type HabitDayPadTrack = {
  sectionId: number;
  lessons: ILesson[];
  scheduled: IUserScheduled;
};

function sortLessonsForDayPad(lessons: ILesson[]): ILesson[] {
  return Array.from(new Map(lessons.map((lesson) => [lesson.id, lesson])).values()).sort(
    (a, b) =>
      Number(a.sequence_number__lesson || 0) - Number(b.sequence_number__lesson || 0) ||
      Number(a.id || 0) - Number(b.id || 0)
  );
}

/**
 * Треки day-pad: модули 1…текущий, только текущая ступень (round).
 * В каждом модуле — все дни круга (без фильтра только по завершённым).
 */
export function buildHabitDayPadTracks(
  sections: ISection[],
  currentSection: ISection,
  habitRound: number,
  currentLessons: ILesson[],
  currentScheduled: IUserScheduled
): HabitDayPadTrack[] {
  const round = Number(habitRound);
  const currentSeq = Number(currentSection.sequence_number__section || 0);
  const sorted = [...sections].sort(
    (a, b) =>
      Number(a.sequence_number__section || 0) - Number(b.sequence_number__section || 0) ||
      Number(a.id || 0) - Number(b.id || 0)
  );

  const tracks: HabitDayPadTrack[] = [];

  for (const section of sorted) {
    const seq = Number(section.sequence_number__section || 0);
    if (seq > currentSeq) break;

    const isCurrent = Number(section.id) === Number(currentSection.id);
    const scheduled = isCurrent
      ? currentScheduled
      : getScheduleForSectionRound(section, round);
    if (!scheduled) continue;

    const lessons = isCurrent
      ? currentLessons
      : resolveLessonsForScheduledRound(section, round).lessons;
    if (lessons.length === 0) continue;

    tracks.push({
      sectionId: Number(section.id),
      lessons: sortLessonsForDayPad(lessons),
      scheduled,
    });
  }

  return tracks;
}

export function getNextSectionBySequence(
  sections: ISection[],
  current: ISection
): ISection | undefined {
  const seq = Number(current.sequence_number__section || 0);
  const sorted = [...sections].sort(
    (a, b) => Number(a.sequence_number__section || 0) - Number(b.sequence_number__section || 0)
  );
  return sorted.find((s) => Number(s.sequence_number__section || 0) > seq);
}

export function prewiusScheduled(
  sections: ISection[]
): IUserScheduled | undefined {
  const today = new Date();
  const all_sched: IUserScheduled[] = [];
  sections.forEach((s) => {
    s.UserLessonsScheduled.forEach((u) => {
      all_sched.push(u);
    });
  });
  all_sched.sort((a, b) => +new Date(a.start_date) - +new Date(b.start_date));
  for (let i = 0; i < all_sched.length; i++) {
    const date = new Date(all_sched[i].start_date);
    var h = today.getTimezoneOffset() / 60;
    date.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    if (+date > +today) {
      if (i < 2) {
        return;
      }
      return all_sched[i - 2];
    }
  }
  return all_sched[all_sched.length - 2];
}

export function getTodayLesson(sections: ISection[]): ILesson {
  const latest = getLatestStartedSchedule(sections) ?? getCurrentScheduled(sections);
  const currentSection = getCurrentSection(sections, latest); //активный раздел курса
  const currentShed =
    getActiveScheduleForSection(currentSection) ?? latest; //текущий круг для этого модуля

  const round = currentShed.round; //текущий round
  const today = new Date(); //текущая дата и время
  const targetDate = new Date(currentShed.start_date); //дата начала прохождения круга
  var h = today.getTimezoneOffset() / 60;
  targetDate.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0); //время начала занятий

  //разница между текущей датой и датой начала в днях.
  // getTime() возвращает timestamp в миллисекундах.
  // (1000 * 3600 * 24) преобразует миллисекунды в дни.
  const daysDifference = Math.floor(
    Math.abs((today.getTime() - targetDate.getTime()) / (1000 * 3600 * 24))
  );

  const lessons = currentSection.Lesson_SectionRelation.filter(
    (l) => l.round === round
  );
  lessons.sort((a, b) => a.sequence_number__lesson - b.sequence_number__lesson);

  return lessons[daysDifference];
}

export function hasGold(section: ISection): boolean {
  const achiv = [];
  for (let i = 0; i < section.UserLessonsScheduled.length; i++) {
    const shed = section.UserLessonsScheduled[i];
    for (let j = 0; j < shed.AchievementsRelation.length; j++) {
      achiv.push(shed.AchievementsRelation[j]);
    }
  }
  for (let i = 0; i < achiv.length; i++) {
    if (achiv[i].typeAchievements === "Gold") {
      return true;
    }
  }
  return false;
}

export function hasPearl(section: ISection): boolean {
  const achiv = [];
  for (let i = 0; i < section.UserLessonsScheduled.length; i++) {
    const shed = section.UserLessonsScheduled[i];
    for (let j = 0; j < shed.AchievementsRelation.length; j++) {
      achiv.push(shed.AchievementsRelation[j]);
    }
  }
  for (let i = 0; i < achiv.length; i++) {
    if (achiv[i].typeAchievements === "Pearl") {
      return true;
    }
  }
  return false;
}
